import { NarratorCard } from '@/components/narrator-card';
import { MediaPackType } from '@/utils/media/types';
import { MediaPackNarrator } from '@calm/evexia-web-sdk';
import { PaginationSentinel } from '@rhg/components/pagination-sentinel';
import { ROUNDED_CARD_SIZE } from '@rhg/components/rounded-card';
import { useClientPagination } from '@rhg/hooks/use-client-pagination';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { Box, Theme } from '@rhg/libs/material';

interface NarratorPackGridProps {
    pack?: MediaPackType;
}

const isNarratorItem = (
    item: MediaPackType['items'][number]
): item is MediaPackNarrator => item?.type === 'narrator';

// Wrapping grid of fixed-size circular cards — auto-fill columns keyed to the
// rounded-card width so it reads like the `/discover/pack/[packClass]` grid
// while matching the `rounded-carousel` card size/spacing. `auto-fill` +
// `minmax` lets the sentinel (which spans `1 / -1`) sit on its own full-width
// row.
const styleGrid = {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: `repeat(auto-fill, minmax(${ROUNDED_CARD_SIZE}px, 1fr))`,
    justifyItems: 'center',
    gap: (theme: Theme) => `${theme.semantics.Spacing[32]}px`,
};

/**
 * Renders a pack of narrator items (e.g. the "Artists" music pack) as a grid of
 * {@link NarratorCard}s. Client-paginates so only a batch of cards mounts at
 * once — the "Artists" pack can carry well over 100 narrators.
 */
export const NarratorPackGrid = ({ pack }: NarratorPackGridProps) => {
    const isMobile = useIsMobile();
    const narrators = pack?.items?.filter(isNarratorItem) ?? [];
    const { visibleItems, hasMore, isRevealing, sentinelRef } =
        useClientPagination(narrators);
    // Eager-load the above-the-fold cards: 1 on mobile, first 3 on desktop.
    const eagerCount = isMobile ? 1 : 3;

    return (
        <Box sx={styleGrid}>
            {visibleItems.map((narrator, index) => (
                <NarratorCard
                    key={narrator.narratorId}
                    narrator={narrator}
                    priority={index < eagerCount}
                />
            ))}
            {hasMore && (
                <PaginationSentinel ref={sentinelRef} isLoading={isRevealing} />
            )}
        </Box>
    );
};
